import {ref, computed, watch} from '@vue/composition-api'
import {useToast} from 'vue-toastification/composition'
import ToastificationContent
  from '@core/components/toastification/ToastificationContent.vue'
import store from '@/store'
import ability from '@/libs/acl/ability'
import {debounce} from 'lodash'
import useFilterCounter from '@/components/Filters/useFiltersCount'
import moment from 'moment-timezone'

export default function useList() {
  const refListTable = ref(null)

  const toast = useToast()

  store.dispatch('unit/getAll', {with: 'router:id,id_tenant'});
  store.dispatch('center/getAll');
  store.dispatch('sector/getAll');
  store.dispatch('omniview_reseller/getAll')
  store.dispatch('dmp_profiles/getAll')

  const facilitiesOptions = computed(() => store.getters['unit/getSelectedItems']);
  const centerOptions = computed(() => store.getters['center/getSelectedItems']);
  const sectorOptions = computed(() => store.getters['sector/getSelectedItems']);
  const resellerOptions = computed(() => store.getters['omniview_reseller/getSelectedItems'])
  const profileOptions = computed(() => store.getters['dmp_profiles/getSelectedItems'])

  const facilitiesFilter = ref(null);
  const centerFilter = ref(null);
  const sectorFilter = ref(null);
  const profileFilter = ref(null)
  const facetedFilters = ref({})
  const customQueryParams = ref({})


  const tableColumns = [

    {label: 'router status', key: 'router_status'},
    {label: 'panel status', key: 'panel_status'},
    {label: 'arm status', key: 'arm_status'},
    {label: 'arming action', key: 'arming_action'},
    {label: 'dmp.name', key: 'name', sortable: true},
    {label: 'dmp.panelHost', key: 'panel_host', sortable: true},
    {label: 'dmp.uuid', key: 'uuid', sorteable: false},
    {label: 'dmp.panelAccount', key: 'panel_account', sortable: true},
    {label: 'dmp.panelRemoteKey', key: 'panel_remote_key', sortable: true},
    //{ label: 'dmp.servers', key: 'server.name', sorteable: false },
    {label: 'dmp.units', key: 'unit.description', sorteable: false},
    {label: 'unit.rems', key: 'rems', sorteable: false},
    {label: 'unit.sirh', key: 'sirh', sorteable: false},
    {label: 'router.ip_vpn', key: 'camera_proxy_router.camera.ip_vpn', sorteable: false},
    {label: 'dmp.center', key: 'center.name', sorteable: false},
    {label: 'dmp.sector', key: 'sector.name', sorteable: false},
    { label: "region.title", key: "region", sortable: true },
    { label: "municipality.title", key: "municipality", sortable: true },
    {label: 'dmp.profile_name', key: 'profile.name', sortable: false},
    {label: 'dmp.panel_model', key: 'panel_model', sorteable: false},
    {
      label: 'dmp.panel_mac_addr',
      key: 'panel_mac_addr',
      sorteable: false,
      formatter: (value) => {
        if (!value) return '';
        const cleanMac = value.replace(/[^0-9A-Fa-f]/g, '');
        if (cleanMac.length !== 12) return value;
        return cleanMac.match(/.{2}/g).join(':').toUpperCase();
      }
    },
    {label: 'dmp.panel_serial_num', key: 'panel_serial_num', sorteable: false},
    {label: 'dmp.instance_host', key: 'instance_host', sorteable: false},
    {label: 'dmp.status', key: 'status', sorteable: false},
    {label: 'date.created_at', key: 'created_at', sortable: true, formatter: 'formatDateAssigned'},
    {label: 'date.updated_at', key: 'updated_at', sortable: true, formatter: 'formatDateAssigned'},
    /* { label: 'dmp.listening_port', key: 'panel_listening_port', sorteable: false }, */
    {label: 'dmp.enabled_zones_audit', key: 'enabled_zones_audit', sorteable: false},
    {label: 'dmp.days_zones_audit', key: 'days_zones_audit', sorteable: false},
    {label: 'dmp.enabled_areas_audit', key: 'enabled_areas_audit', sorteable: false},
    {label: 'dmp.days_areas_audit', key: 'days_areas_audit', sorteable: false},
  ]

  if (ability.can('update', 'Dmp') || ability.can('delete', 'Dmp')) {
    tableColumns.splice(0, 0, {label: 'actions.name', key: 'actions'})
  }

  const perPage = ref(10)
  const totalList = ref(0)
  const currentPage = ref(1)
  const perPageOptions = [10, 25, 50, 100]
  const searchQuery = ref('')
  const sortBy = ref('id')
  const isSortDirDesc = ref(true)
  const action = ref('list')

  const dataMeta = computed(() => {
    const localItemsCount = refListTable.value ? refListTable.value.localItems.length : 0
    return {
      from: perPage.value * (currentPage.value - 1) + (localItemsCount ? 1 : 0),
      to: perPage.value * (currentPage.value - 1) + localItemsCount,
      of: totalList.value,
    }
  })

  const fetchList = () => {
    return store.dispatch('dmp_panels/getAll', {
      q: searchQuery.value,
      max: perPage.value,
      'page[n]': currentPage.value,
      sortBy: sortBy.value,
      sortDesc: isSortDirDesc.value,
      center: centerFilter.value,
      sector: sectorFilter.value,
      units: facilitiesFilter.value,
      id_dmp_profile: profileFilter.value,
      ...facetedFilters.value,
      action: action.value,
      ...customQueryParams.value,
    })
      .then(response => {
        if (action.value === 'export') {
          action.value = 'list'
          return []
        } else {
          totalList.value = response.meta.total
          return response.data
        }
      })
      .catch(() => {
        action.value = 'list'
        toast({
          component: ToastificationContent,
          props: {
            title: 'Error obteniendo la información',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
        return []
      })
  }


  const refetchData = () => {
    refListTable.value.refresh()
  }

  watch([currentPage, perPage,], () => {
    refetchData()
  })

  watch([searchQuery], debounce(() => {
    refetchData()
  }, 500))

  // filters counter ============================
  const filters = ref({});
  const filtersCount = useFilterCounter(filters);
  const buildFilters = () => {
    filters.value = {
      q: searchQuery.value,
      center: centerFilter.value,
      sector: sectorFilter.value,
      units: facilitiesFilter.value,
      id_dmp_profile: profileFilter.value,
      ...facetedFilters.value,
      ...customQueryParams.value,
    }
    refetchData();
  }

  const clearSearchFilters = () => {
    searchQuery.value = null;
    centerFilter.value = null;
    sectorFilter.value = null;
    facilitiesFilter.value = null;
    profileFilter.value = null;
    facetedFilters.value = {};
    customQueryParams.value = {};

    buildFilters();
  }
  const setFacetedFilters = values => {
    facetedFilters.value = { ...values, advanced_search: true }
    refetchData()
  }

  const formatDateAssigned = value => {
    const formattedDate = moment(value).format('DD/MM/YYYY HH:mm:ss')
    return formattedDate
  }

  store.dispatch("municipality/getAll");
  const municipalityOptions = computed(
    () => store.getters["municipality/getSelectedItems"]
  );

  store.dispatch("region/getAll");
  const regionOptions = computed(() => store.getters["region/getSelectedItems"]);
  // ============================================

  return {
    tableColumns,
    perPage,
    currentPage,
    totalList,
    fetchList,
    dataMeta,
    perPageOptions,
    searchQuery,
    sortBy,
    isSortDirDesc,
    refListTable,
    centerFilter,
    sectorFilter,
    facilitiesFilter,
    profileFilter,
    facilitiesOptions,
    centerOptions,
    sectorOptions,
    resellerOptions,
    profileOptions,
    refetchData,

    filtersCount,
    buildFilters,
    clearSearchFilters,
    action,
    setFacetedFilters,
    formatDateAssigned,
    customQueryParams,
    municipalityOptions,
    regionOptions,
  }
}
